/**
 * 狙撃カム（ポケットイン演出）の判定ヘルパー（自前・GPL 非依存）。
 *
 * 役割: ショット直前の「狙うポケット決定」「3点がビューに収まっているか判定」「明らかに
 * 外れたか判定」のみを純粋関数で提供する。カメラ操作・フリーズ制御本体は `camera-rig.ts`、
 * フレーム配線は `main.ts` が担う。
 *
 * GPL 非依存: 物理エンジン・vendor 描画には依存しない。THREE は数学型（Vector3/Frustum/Matrix4）
 * の利用に限る（CLAUDE.md / system-design-traceability.md §2 の方針）。
 *
 * 設計トレーサビリティ: F-03（カメラ）・F-10（HUD/設定）・M-05（シミュレーション補助）。
 */

import * as THREE from 'three';

/** 2D 位置（メートル）。 */
export interface Vec2Like {
  readonly x: number;
  readonly y: number;
}

/** カメラ視錐台に対して点が画面内か判定する。z=0（テーブル平面）の点として評価する。
 *  各点を半径 `marginM` の球（z=0 上の円）として判定し、球が視錐台と交差すれば「画面内」とする。
 *  marginM=0 なら点中心のみ厳密判定（containsPoint 相当）。
 *  狙撃カムでは marginM=ballRadius*N（例: 4R≈0.13m）で「ボール球の一部でも画面内」を発動条件にし、
 *  ポケットが画面端ギリギリで境界判定 fail するケースを救済する。 */
export const pointsInFrustum = (
  camera: THREE.PerspectiveCamera,
  points: ReadonlyArray<Vec2Like>,
  marginM: number = 0,
): boolean => {
  if (points.length === 0) return true;
  // updateMatrixWorld を呼ばないと projection が古い可能性がある。呼び出し側で update 済みでも安全。
  camera.updateMatrixWorld();
  const m = new THREE.Matrix4().multiplyMatrices(
    camera.projectionMatrix,
    camera.matrixWorldInverse,
  );
  const frustum = new THREE.Frustum().setFromProjectionMatrix(m);
  if (marginM <= 0) {
    const v = new THREE.Vector3();
    for (const p of points) {
      v.set(p.x, p.y, 0);
      if (!frustum.containsPoint(v)) return false;
    }
    return true;
  }
  const sphere = new THREE.Sphere(new THREE.Vector3(), marginM);
  for (const p of points) {
    sphere.center.set(p.x, p.y, 0);
    if (!frustum.intersectsSphere(sphere)) return false;
  }
  return true;
};

/** 手玉→的玉の延長線で最近接のポケットを返す（自動照準 OFF 時のフォールバック）。
 *  延長線（的玉から見て、手玉とは反対方向）上で最も近いポケットを採用する。
 *  ポケット配列はテーブル幾何（world.ts getPocketAims など）から受け取る。
 *  該当が無い（後方にしかポケットがない場合等）は null。 */
export const fallbackTargetPocket = (
  cue: Vec2Like,
  target: Vec2Like,
  pockets: ReadonlyArray<Vec2Like>,
): { index: number; pos: Vec2Like } | null => {
  // 的玉から見た「手玉とは反対方向」の単位ベクトル
  const dx = target.x - cue.x;
  const dy = target.y - cue.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  const ux = dx / len;
  const uy = dy / len;
  // 各ポケットについて「target から見た方位」と延長方向の内積（cosθ）を計算。
  // cosθ が最大（≥ COS_THRESHOLD）かつ距離が最小に近いものを採用。
  // 前方扇形（±60° = cos 0.5 以上）に限定して「明らかに後方」のポケットを除外。
  const COS_THRESHOLD = 0.5;
  let bestIdx = -1;
  let bestScore = -Infinity;
  for (let i = 0; i < pockets.length; i++) {
    const p = pockets[i];
    if (!p) continue;
    const vx = p.x - target.x;
    const vy = p.y - target.y;
    const vlen = Math.hypot(vx, vy);
    if (vlen < 1e-6) continue;
    const cosTheta = (vx * ux + vy * uy) / vlen;
    if (cosTheta < COS_THRESHOLD) continue;
    // 同じ前方扇形内では「より直進方向（cos が大）」を優先しつつ、距離が近い方を加点。
    // スコア = cosθ * (1 / (1 + vlen))（近距離+直進が高スコア）
    const score = cosTheta * (1 / (1 + vlen));
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }
  if (bestIdx < 0) return null;
  const p = pockets[bestIdx];
  if (!p) return null;
  return { index: bestIdx, pos: { x: p.x, y: p.y } };
};

/** ヒット直後の的玉方向と「狙うポケットへの方位」の角度差が閾値超なら「明らかに外れ」。
 *  単位: 度。閾値 SHOT_FREEZE_MISS_DEG=12°（クッション反射経路は救わず、達成感の演出
 *  維持を優先する設計）。
 *  - targetVel が 0 ベクトルなら判定不可（false を返す＝外れと断定しない）。
 *  - target → pocket の単位ベクトルとの cos が閾値より小さければ true。 */
export const SHOT_FREEZE_MISS_DEG = 12;

export const isClearlyMissingPocket = (
  target: Vec2Like,
  targetVel: Vec2Like,
  pocket: Vec2Like,
  thresholdDeg: number = SHOT_FREEZE_MISS_DEG,
): boolean => {
  const vlen = Math.hypot(targetVel.x, targetVel.y);
  if (vlen < 1e-6) return false; // 動いていない＝判定不能
  const ux = targetVel.x / vlen;
  const uy = targetVel.y / vlen;
  const dx = pocket.x - target.x;
  const dy = pocket.y - target.y;
  const dlen = Math.hypot(dx, dy);
  if (dlen < 1e-6) return false;
  const px = dx / dlen;
  const py = dy / dlen;
  const cosTheta = ux * px + uy * py;
  // 閾値を cos に変換: cos(threshold) より小なら「外れ」。
  const cosThr = Math.cos((thresholdDeg * Math.PI) / 180);
  return cosTheta < cosThr;
};

/** 狙撃カム解放後の「方向だけ」追従フレーミングの算出（半径・仰角は変更しない）。
 *
 *  目的（ユーザー要望 2026-06-18d）: 的玉ポケットイン後、**手前から奥へ「手玉→次の的玉→次球を
 *  入れるポケット」が並ぶ depth 構図**で手玉の動きを追う。次ショットの狙撃ビュー（プレビュー）に
 *  相当する構図で、ユーザーが「次は何をどう撞くか」を視覚的に把握できる。
 *
 *  - **lookAt**: 手玉位置（画面中央に手玉）。手玉が動くと lookAt も追従し、camera は手玉に対して
 *    azimuth/elevation/radius の相対関係を保つ＝手玉中心の depth 視点が維持される。
 *  - **azimuth**: 手玉が画面手前、ポケット P が画面奥に映るよう camera を P の反対側（cue 後方）に
 *    置く。`camera position = lookAt + r·[cos(e)·cos(az), cos(e)·sin(az), sin(e)]` で
 *    camera 位置を cue から「pocket と逆方向」に置きたいので
 *    `az = atan2(cue.y - pocket.y, cue.x - pocket.x)`。`alignToAim(aimAngle)` は内部で +π するため、
 *    呼び出し側は `alignToAim(atan2(pocket.y - cue.y, pocket.x - cue.x))` を渡せば同じ azimuth に到達する。
 *  - **pocket**: 次の的玉を「現在の cue 位置から狙うとどのポケットに入る向きか」＝
 *    `fallbackTargetPocket(cue, next, pockets)` で算出した次球の入射延長線上のポケットを渡す。
 *    （呼び出し側 main.ts が `getPocketAims()` で実物理のポケット位置を渡し fallbackTargetPocket で
 *    最良候補を選ぶ）
 *  - `pocket=null` のときは「次球ポケットが特定できない」状態。lookAt のみ手玉に寄せ、
 *    azimuth は呼び出し側で維持する判断（`azimuth=null` を返す）。 */
export const computePostFreezeFraming = (
  cue: Vec2Like,
  pocket: Vec2Like | null,
): { lookAt: Vec2Like; azimuth: number | null } => {
  if (!pocket) {
    return { lookAt: { x: cue.x, y: cue.y }, azimuth: null };
  }
  const dx = pocket.x - cue.x;
  const dy = pocket.y - cue.y;
  if (Math.hypot(dx, dy) < 1e-4) {
    // cue ≒ pocket: 方位が不定。lookAt のみ手玉。
    return { lookAt: { x: cue.x, y: cue.y }, azimuth: null };
  }
  // camera 位置を「cue から pocket と逆方向」へ置く azimuth。
  // (camera relative to lookAt = (cue.x - pocket.x, cue.y - pocket.y) 方向)
  const azimuth = Math.atan2(cue.y - pocket.y, cue.x - pocket.x);
  return { lookAt: { x: cue.x, y: cue.y }, azimuth };
};
